import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";
import { resolveImageUrl } from "../lib/images";
import { Minus, Plus, ShoppingBag, ArrowLeft, Check } from "lucide-react";
import { toast } from "sonner";

export default function ProductDetail() {
    const { id } = useParams();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [qty, setQty] = useState(1);
    const [selectedImg, setSelectedImg] = useState(0);
    const { addItem } = useCart();
    const navigate = useNavigate();

    useEffect(() => {
        setLoading(true);
        setSelectedImg(0);
        api.get(`/products/${id}`)
            .then((r) => setProduct(r.data))
            .catch(() => setProduct(null))
            .finally(() => setLoading(false));
    }, [id]);

    if (loading) return <p className="text-center pt-40 text-taupe">Cargando...</p>;
    if (!product)
        return (
            <div className="text-center pt-40 pb-20">
                <p className="font-serif text-3xl">Pieza no encontrada</p>
                <Link to="/catalogo" className="text-rose underline mt-4 inline-block">
                    Volver al catálogo
                </Link>
            </div>
        );

    function handleAdd() {
        addItem(product, qty);
        toast.success("Añadido al carrito", {
            description: `${product.name} × ${qty}`,
        });
    }

    return (
        <div className="px-6 md:px-12 lg:px-20 pt-32 pb-20" data-testid="product-detail-page">
            <button
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-2 text-sm text-taupe hover:text-rose mb-8"
                data-testid="back-btn"
            >
                <ArrowLeft strokeWidth={1.5} className="w-4 h-4" /> Volver
            </button>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
                <div className="space-y-4" data-testid="product-gallery">
                    <div className="aspect-square rounded-2xl overflow-hidden bg-blush">
                        {product.images?.[selectedImg] ? (
                            <img
                                src={resolveImageUrl(product.images[selectedImg])}
                                alt={product.name}
                                className="w-full h-full object-cover"
                            />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center text-taupe">
                                Sin imagen
                            </div>
                        )}
                    </div>
                    {product.images?.length > 1 && (
                        <div className="grid grid-cols-5 gap-3">
                            {product.images.map((img, i) => (
                                <button
                                    key={i}
                                    onClick={() => setSelectedImg(i)}
                                    className={`aspect-square rounded-lg overflow-hidden border-2 transition ${
                                        selectedImg === i ? "border-rose" : "border-transparent"
                                    }`}
                                    data-testid={`thumbnail-${i}`}
                                >
                                    <img src={resolveImageUrl(img)} alt="" className="w-full h-full object-cover" />
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <div className="space-y-6">
                    <p className="overline">{product.category}</p>
                    <h1
                        className="font-serif text-5xl sm:text-6xl text-charcoal leading-tight"
                        data-testid="product-name"
                    >
                        {product.name}
                    </h1>
                    <p className="text-3xl font-serif text-rose" data-testid="product-price">
                        ${product.price.toFixed(2)}
                    </p>
                    <p className="text-base text-taupe leading-relaxed">{product.description}</p>

                    <div className="flex items-center gap-2 text-sm">
                        <Check strokeWidth={1.5} className="w-4 h-4 text-rose" />
                        <span className="text-taupe">
                            {product.stock > 0
                                ? `${product.stock} unidades disponibles`
                                : "Agotado"}
                        </span>
                    </div>

                    <div className="pt-4 flex items-center gap-4">
                        <div className="flex items-center border border-border-soft rounded-full">
                            <button
                                onClick={() => setQty((q) => Math.max(1, q - 1))}
                                className="p-3 hover:text-rose"
                                data-testid="qty-decrement"
                            >
                                <Minus strokeWidth={1.5} className="w-4 h-4" />
                            </button>
                            <span className="w-10 text-center" data-testid="qty-value">
                                {qty}
                            </span>
                            <button
                                onClick={() => setQty((q) => q + 1)}
                                className="p-3 hover:text-rose"
                                data-testid="qty-increment"
                            >
                                <Plus strokeWidth={1.5} className="w-4 h-4" />
                            </button>
                        </div>
                        <button
                            onClick={handleAdd}
                            disabled={product.stock === 0}
                            className="flex-1 bg-rose text-white hover:bg-rose-dark rounded-full px-8 py-4 transition-colors text-xs tracking-[0.2em] uppercase inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            data-testid="add-to-cart-btn"
                        >
                            <ShoppingBag strokeWidth={1.5} className="w-4 h-4" />
                            Añadir al carrito
                        </button>
                    </div>

                    <div className="border-t border-border-soft pt-6 mt-8 space-y-3 text-sm text-taupe">
                        <p>• Envío gratis en pedidos superiores a $50</p>
                        <p>• Empaquetado especial listo para regalar</p>
                        <p>• Atención personalizada</p>
                    </div>
                </div>
            </div>
        </div>
    );
}
